/* eslint-disable react/jsx-no-useless-fragment */
import DoneIcon from '@mui/icons-material/Done';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlined from '@mui/icons-material/LockOutlined';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Avatar, Box, IconButton, Tooltip } from '@mui/material';
import CheckCircleGrayIcon from 'assets/images/check-circle-gray.svg';
import React from 'react';

import AssignUserModal from '../../AssignUserModal/AssignUserModal';
import StylishLoader from '../../components/StylishLoader/StylishLoader';
import { JobAssignStatus, JobQueueTableType } from '../../enum/TeamUserRoles';

interface JobHeaderActionsProps {
  jobInfo: any;
  userDetail: any;
  refetch: any;
  tableType: JobQueueTableType;
  timeLeft: string;
  isAccepting: boolean;
  handleAccept: (e: React.MouseEvent) => void;
  setSettingsOpen: (open: boolean) => void;
}

const JobHeaderActions: React.FC<JobHeaderActionsProps> = ({
  jobInfo,
  userDetail,
  refetch,
  tableType,
  timeLeft,
  isAccepting,
  handleAccept,
  setSettingsOpen,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isAssigning, setIsAssigning] = React.useState(false);

  const showTimerAndAssign =
    (tableType === JobQueueTableType.TEAM_LEAD_NEW &&
      jobInfo?.jobAssignStatus === JobAssignStatus.TL_ASSIGNED) ||
    (tableType === JobQueueTableType.RECRUITER_NEW &&
      jobInfo?.jobAssignStatus === JobAssignStatus.RECRUITER_ASSIGNED);

  const showOnlyAssign =
    (tableType === JobQueueTableType.MANAGER_UNALLOCATED ||
      tableType === JobQueueTableType.BUSINESS_HEAD_UNALLOCATED ||
      tableType === JobQueueTableType.MANAGEMENT_JQ_UNALLOCATED ||
      tableType === JobQueueTableType.RECRUITER_NEW ||
      tableType === JobQueueTableType.SUPPORT_TL_NEW) &&
    (jobInfo?.jobAssignStatus === JobAssignStatus.RECRUITER_UNALLOCATED ||
      jobInfo?.jobAssignStatus === JobAssignStatus.TL_UNALLOCATED);

  const showExternalSearchShow =
    tableType === JobQueueTableType.RECRUITER_PENDING ||
    tableType === JobQueueTableType.RECRUITER_SUBMITTED ||
    (tableType === JobQueueTableType.RECRUITER_NEW &&
      jobInfo?.jobAssignStatus === JobAssignStatus.RECRUITER_ACCEPTED);

  const ActionBtn = ({ label, icon, onClick, loading, showArrow }: any) => (
    <Box className="assign-button" onClick={onClick}>
      {loading ? (
        <StylishLoader mode="button" text="" />
      ) : (
        <>
          {icon}
          <span className="assign-text">{label}</span>
          {showArrow && <ExpandMoreIcon className="arrow-icon" />}
        </>
      )}
    </Box>
  );

  return (
    <Box className="actions-area">
      {showExternalSearchShow ? (
        jobInfo?.allowEmailCampaign || jobInfo?.allowJobBoardSearch ? (
          <Box className="external-search-enabled">
            <img
              src={CheckCircleGrayIcon}
              alt="Enabled"
              className="enabled-icon"
            />
            <span className="enabled-text">External Search Enabled</span>
          </Box>
        ) : (
          <Tooltip
            title="Requires Team Lead approval. Contact your lead to enable external search for this job."
            arrow
            placement="bottom"
            classes={{
              arrow: 'job-header-tooltip-arrow',
              tooltip: 'job-header-tooltip',
            }}>
            <Box className="external-search-locked">
              <LockOutlined className="lock-icon" />
              <span className="locked-text">External Search Locked</span>
            </Box>
          </Tooltip>
        )
      ) : showTimerAndAssign ? (
        <>
          {timeLeft && (
            <Box className="timer-button">
              <TimerOutlinedIcon className="timer-icon" />
              <span>{timeLeft}</span>
            </Box>
          )}
          {timeLeft !== 'Expired' && (
            <>
              {tableType === JobQueueTableType.RECRUITER_NEW ? (
                <ActionBtn
                  label="Accept"
                  icon={<DoneIcon className="assign-icon" />}
                  onClick={handleAccept}
                  loading={isAccepting}
                />
              ) : (
                <Box
                  className="assign-button"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                  }}>
                  {isAssigning ? (
                    <StylishLoader mode="button" text="" />
                  ) : (
                    <>
                      <PersonAddAlt1Icon className="assign-icon" />
                      <span className="assign-text">Assign</span>
                      <ExpandMoreIcon className="arrow-icon" />
                    </>
                  )}
                </Box>
              )}
            </>
          )}
        </>
      ) : showOnlyAssign ? (
        <Box
          className="assign-button"
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}>
          {isAssigning ? (
            <StylishLoader mode="button" text="" />
          ) : (
            <>
              <PersonAddAlt1Icon className="assign-icon" />
              <span className="assign-text">Assign</span>
              <ExpandMoreIcon className="arrow-icon" />
            </>
          )}
        </Box>
      ) : (
        <>
          <Box className="user-selector-container">
            {jobInfo?.usersAssigned?.length > 0 && (
              <>
                <Box className="user-info-side">
                  <Avatar
                    className="user-avatar"
                    src={jobInfo?.usersAssigned[0]?.assignedToImageId || ''}>
                    {String(
                      jobInfo.usersAssigned[0].assignedToFirstName?.[0] || '',
                    )}
                    {String(
                      jobInfo.usersAssigned[0].assignedToLastName?.[0] || '',
                    )}
                  </Avatar>
                  <span className="user-name">
                    {`${String(
                      jobInfo.usersAssigned[0].assignedToFirstName || '',
                    )} ${String(
                      jobInfo.usersAssigned[0].assignedToLastName || '',
                    )}`}
                  </span>
                </Box>
                <Box className="arrow-side">
                  <ExpandMoreIcon className="arrow-icon" />
                </Box>
              </>
            )}
          </Box>
          <IconButton
            className="settings-button"
            onClick={() => setSettingsOpen(true)}>
            <SettingsOutlinedIcon className="settings-icon" />
          </IconButton>
        </>
      )}
      {anchorEl && (
        <AssignUserModal
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          refetch={refetch}
          setIsAssigning={setIsAssigning}
          anchorEl={anchorEl}
          userDetail={userDetail}
          jobData={jobInfo}
        />
      )}
    </Box>
  );
};

export default JobHeaderActions;
